import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { DragDropContext } from 'react-beautiful-dnd';
import LeadModal from '../components/marketing/LeadModal';
import AgendamentoLeadModal from '../components/marketing/AgendamentoLeadModal';
import MarketingKanbanColumn from '../components/marketing/MarketingKanbanColumn';
import WhatsAppChat from '../components/marketing/WhatsAppChat';
import PipelinesConfig from '../components/marketing/PipelinesConfig';
import '../styles/marketing.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEntidadeContext } from '../EntidadeContext';
import axios from 'axios';
import { useAlertModal } from '../AlertModalContext';
import { useAuthContext } from '../context/AuthContext';
import { useUserContext } from '../UserContext';
import LeadImportModal from '../components/marketing/LeadImportModal';
import LeadFiltros from '../components/marketing/LeadFiltros';
import SalesCommissionsPanel from '../components/marketing/SalesCommissionsPanel';
import LeadAutomationDashboardModal from '../components/marketing/LeadAutomationDashboardModal';
import CommercialDashboard from '../components/marketing/CommercialDashboard';
import WhatsAppCampaigns from '../components/marketing/WhatsAppCampaigns';

const LEADS_PER_PAGE = 10;

const CrmPage = () => {
    const pageKey = 'crm';
    const { showAlertModal } = useAlertModal();
    const { loggedInUser: currentUser } = useAuthContext();
    const { currentUserRole, getPermittedInternalTabs, isLoadingAccessConfig } = useUserContext();

    const [leads, setLeads] = useState([]);
    const [leadsPorEstagio, setLeadsPorEstagio] = useState({});
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [error, setError] = useState(null);
    const [pipelines, setPipelines] = useState([]);
    const [pipelineAtiva, setPipelineAtiva] = useState(null);
    const [isLoadingPipeline, setIsLoadingPipeline] = useState(true);
    const [showLeadModal, setShowLeadModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [isSavingLead, setIsSavingLead] = useState(false);
    const [showAgendamentoModal, setShowAgendamentoModal] = useState(false);
    const [pacienteParaAgendar, setPacienteParaAgendar] = useState(null);
    const [showPipelinesConfig, setShowPipelinesConfig] = useState(false);
    const [whatsAppLead, setWhatsAppLead] = useState(null);
    const [showWhatsAppChat, setShowWhatsAppChat] = useState(false);
    const [marketingProfileIds, setMarketingProfileIds] = useState({ standard: [], custom: [] });
    const [activeTab, setActiveTab] = useState('');
    const [isMobileKanban, setIsMobileKanban] = useState(
        () => window.matchMedia('(max-width: 575px)').matches
    );
    const [mobileStageId, setMobileStageId] = useState('');
    const [showImportModal, setShowImportModal] = useState(false);

    // ✅ Estado separado para o input de busca (imediato) e indicador visual
    const [buscaInput, setBuscaInput] = useState('');
    const [isBuscando, setIsBuscando] = useState(false);

    const { currentEntidade, supportData, isLoadingSupportData, agendaConfig, isLoadingAgendaConfig } = useEntidadeContext();
    const activeModules = currentEntidade?.modulos_ativos;
    const hasCampaignsModule = !Array.isArray(activeModules) || activeModules.includes('campaigns');

    const permittedTabs = useMemo(() => {
        return [...getPermittedInternalTabs(pageKey, currentUserRole)]
            .filter(tab => tab.key !== 'crm_campaigns' || hasCampaignsModule)
            .sort((a, b) => {
            const order = { crm_dashboard: 0, mk_funil_leads: 1, crm_campaigns: 2 };
            return (order[a.key] ?? 99) - (order[b.key] ?? 99);
        });
    }, [getPermittedInternalTabs, pageKey, currentUserRole, isLoadingAccessConfig, hasCampaignsModule]);

    // Estado inicial dos filtros
    const FILTROS_INICIAIS = {
        busca:           '',
        responsavel_id:  '',
        status:          '',
        prioridade:      '',
        tag:             '',
        perdido_motivo:  '',
        criado_de:       '',
        criado_ate:      '',
        atualizado_de:   '',
        atualizado_ate:  '',
        fechamento_de:   '',
        fechamento_ate:  '',
    };

    const [filtrosAvancados, setFiltrosAvancados] = useState(FILTROS_INICIAIS);

    const filtrosParaAPIRef = useRef({});

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 575px)');

        const handleMobileChange = event => {
            setIsMobileKanban(event.matches);
        };

        setIsMobileKanban(mediaQuery.matches);

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleMobileChange);
        } else {
            mediaQuery.addListener(handleMobileChange);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleMobileChange);
            } else {
                mediaQuery.removeListener(handleMobileChange);
            }
        };
    }, []);

    useEffect(() => {
        const stages = pipelineAtiva?.estagios || [];

        if (stages.length === 0) {
            setMobileStageId('');
            return;
        }

        const selectedStageExists = stages.some(
            stage => String(stage.id) === String(mobileStageId)
        );

        if (!selectedStageExists) {
            setMobileStageId(String(stages[0].id));
        }
    }, [pipelineAtiva, mobileStageId]);

    // ✅ Debounce da busca — atualiza filtrosAvancados.busca após 600ms sem digitar
    useEffect(() => {
        if (buscaInput === filtrosAvancados.busca) return;

        setIsBuscando(true);
        const timer = setTimeout(() => {
            setFiltrosAvancados(prev => ({ ...prev, busca: buscaInput }));
            setIsBuscando(false);
        }, 600);

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buscaInput]);

    // Contar filtros ativos (excluindo busca que está no header)
    const totalFiltrosAtivos = useMemo(() => {
        const { busca, ...filtrosSemBusca } = filtrosAvancados;
        return Object.values(filtrosSemBusca).filter(v => v !== '').length;
    }, [filtrosAvancados]);

    useEffect(() => {
        if (permittedTabs.length > 0 && !isLoadingAccessConfig) {
            if (!activeTab || !permittedTabs.some(tab => tab.key === activeTab)) {
                setActiveTab(permittedTabs[0].key);
            }
        }
    }, [permittedTabs, activeTab, isLoadingAccessConfig]);

    const fetchPipelines = useCallback(async () => {
        if (!currentEntidade) return;
        setIsLoadingPipeline(true);
        try {
            const response = await axios.get('/api/pipelines');
            const lista = response.data.pipelines || [];
            setPipelines(lista);
            if (!pipelineAtiva) {
                const padrao = lista.find(p => p.is_default) || lista[0];
                setPipelineAtiva(padrao || null);
            } else {
                const atualizada = lista.find(p => p.id === pipelineAtiva.id);
                if (atualizada) setPipelineAtiva(atualizada);
            }
        } catch (err) {
            console.error('Erro ao carregar pipelines:', err);
        } finally {
            setIsLoadingPipeline(false);
        }
    }, [currentEntidade]);

    useEffect(() => {
        if (activeTab === 'mk_funil_leads' && currentEntidade) fetchPipelines();
    }, [activeTab, currentEntidade, fetchPipelines]);

    const fetchLeadsPorEstagio = useCallback(async (pipeline, filtro = '', filtrosExtra = {}, silent = false) => {
        if (!currentEntidade || !pipeline) return;

        if (!silent) {
            setIsLoadingData(true);
        }
        setError(null);

        try {
            // 1. Dados gerais (marketing_profile_ids)
            const geral = await axios.get('/api/leads', {
                params: { entidade_id: currentEntidade.id }
            });
            setLeads(geral.data.leads || []);
            setMarketingProfileIds(
                geral.data.marketing_profile_ids || { standard: [], custom: [] }
            );

            // 2. Página 1 de cada estágio em paralelo
            const estagios = pipeline.estagios || [];
            const resultados = await Promise.all(
                estagios.map(async (estagio) => {
                    const params = {
                        entidade_id: currentEntidade.id,
                        estagio_id:  estagio.id,
                        per_page:    LEADS_PER_PAGE,
                        page:        1,
                    };
                    if (filtro) params.responsavel_id = filtro;

                    // ✅ Todos os filtros incluindo busca vão para a API
                    if (filtrosExtra.responsavel_id) params.responsavel_id = filtrosExtra.responsavel_id;
                    if (filtrosExtra.busca)          params.busca           = filtrosExtra.busca;
                    if (filtrosExtra.status)         params.status          = filtrosExtra.status;
                    if (filtrosExtra.prioridade)     params.prioridade      = filtrosExtra.prioridade;
                    if (filtrosExtra.tag)            params.tag             = filtrosExtra.tag;
                    if (filtrosExtra.perdido_motivo) params.perdido_motivo  = filtrosExtra.perdido_motivo;
                    if (filtrosExtra.criado_de)      params.criado_de       = filtrosExtra.criado_de;
                    if (filtrosExtra.criado_ate)     params.criado_ate      = filtrosExtra.criado_ate;
                    if (filtrosExtra.atualizado_de)  params.atualizado_de   = filtrosExtra.atualizado_de;
                    if (filtrosExtra.atualizado_ate) params.atualizado_ate  = filtrosExtra.atualizado_ate;
                    if (filtrosExtra.fechamento_de)  params.fechamento_de   = filtrosExtra.fechamento_de;
                    if (filtrosExtra.fechamento_ate) params.fechamento_ate  = filtrosExtra.fechamento_ate;

                    const res = await axios.get('/api/leads', { params });
                    return {
                        estagioId: estagio.id,
                        leads:     res.data.leads || [],
                        total:     res.data.total || 0,
                    };
                })
            );

            const mapa = {};
            resultados.forEach(r => {
                mapa[r.estagioId] = { leads: r.leads, total: r.total };
            });
            setLeadsPorEstagio(mapa);

        } catch {
            if (!silent) setError('Não foi possível carregar os leads.');
        } finally {
            if (!silent) setIsLoadingData(false);
        }
    }, [currentEntidade]);

    // ── useEffect 1: Carga inicial (quando aba/pipeline/entidade muda) ──
    useEffect(() => {
        if (isLoadingSupportData || isLoadingAgendaConfig) return;

        if (activeTab === 'mk_funil_leads' && pipelineAtiva) {
            filtrosParaAPIRef.current = filtrosAvancados;
            fetchLeadsPorEstagio(pipelineAtiva, filtrosAvancados.responsavel_id, filtrosAvancados);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, currentEntidade?.id, pipelineAtiva?.id, isLoadingSupportData, isLoadingAgendaConfig]);

    // ── useEffect 2: Recarrega quando filtros mudam (inclui busca com debounce) ──
    useEffect(() => {
        if (activeTab !== 'mk_funil_leads' || !pipelineAtiva) return;

        const anterior = filtrosParaAPIRef.current;
        const mudou = JSON.stringify(filtrosAvancados) !== JSON.stringify(anterior);

        if (!mudou) return;

        filtrosParaAPIRef.current = filtrosAvancados;
        fetchLeadsPorEstagio(pipelineAtiva, filtrosAvancados.responsavel_id, filtrosAvancados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtrosAvancados]);

    // Polling a cada 60s — silencioso para não resetar paginação das colunas
    useEffect(() => {
        if (activeTab !== 'mk_funil_leads' || !currentEntidade || !pipelineAtiva) return;
        const interval = setInterval(() => {
            const filtros = filtrosParaAPIRef.current;
            fetchLeadsPorEstagio(pipelineAtiva, filtros.responsavel_id, filtros, true);
        }, 60000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, currentEntidade?.id, pipelineAtiva?.id]);

    const handleCardClick = async (lead) => {
        setSelectedLead(lead);
        setShowLeadModal(true);

        try {
            const response = await axios.get(`/api/leads/${lead.id}`);
            setSelectedLead(response.data);
        } catch (error) {
            console.error('Erro ao atualizar os dados do lead:', error);
        }
    };
    const handleShowCreateModal = () => { setSelectedLead(null); setShowLeadModal(true); };

    const handleWhatsAppClick = (lead) => {
        if (showWhatsAppChat && whatsAppLead?.id === lead.id) {
            setShowWhatsAppChat(false); setWhatsAppLead(null); return;
        }
        setWhatsAppLead(lead); setShowWhatsAppChat(true);
    };

    const handleSaveLead = async (leadData) => {
        setIsSavingLead(true);
        try {
            if (leadData.id) {
                const response = await axios.put(`/api/leads/${leadData.id}`, leadData);
                if (response.data?.lead) {
                    const updated = response.data.lead;
                    setLeadsPorEstagio(prev => {
                        const novo = { ...prev };
                        Object.keys(novo).forEach(estagioId => {
                            novo[estagioId] = {
                                ...novo[estagioId],
                                leads: novo[estagioId].leads.map(l =>
                                    l.id === updated.id ? updated : l
                                ),
                            };
                        });
                        return novo;
                    });
                }
            } else {
                const response = await axios.post('/api/leads', {
                    ...leadData,
                    entidade_id: currentEntidade.id,
                    pipeline_id: leadData.pipeline_id || pipelineAtiva?.id,
                    estagio_id: leadData.estagio_id || pipelineAtiva?.estagios?.find(e => e.tipo === 'entrada')?.id,
                });
                if (response.data) {
                    const novoLead = response.data.lead || response.data;
                    if (String(novoLead.pipeline_id) === String(pipelineAtiva?.id)) {
                        const estagioId = novoLead.estagio_id;
                        setLeadsPorEstagio(prev => ({
                            ...prev,
                            [estagioId]: {
                                leads: [novoLead, ...(prev[estagioId]?.leads || [])],
                                total: (prev[estagioId]?.total || 0) + 1,
                            },
                        }));
                    } else {
                        const destinationPipeline = pipelines.find(item => String(item.id) === String(novoLead.pipeline_id));
                        if (destinationPipeline) setPipelineAtiva(destinationPipeline);
                    }
                }
            }
            setShowLeadModal(false);
        } catch (error) {
            showAlertModal('Erro ao Salvar', error.response?.data?.message || 'Não foi possível salvar o lead.', 'danger');
        } finally {
            setIsSavingLead(false);
        }
    };

    const onDragEnd = (result) => {
        const { destination, source, draggableId } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const leadId    = parseInt(draggableId);
        const origemId  = parseInt(source.droppableId);
        const destinoId = parseInt(destination.droppableId);

        const leadMovido = leadsPorEstagio[origemId]?.leads.find(l => l.id === leadId);
        if (!leadMovido) return;

        const leadAtualizado = { ...leadMovido, estagio_id: destinoId };

        setLeadsPorEstagio(prev => {
            const novo = { ...prev };
            novo[origemId] = {
                leads: (novo[origemId]?.leads || []).filter(l => l.id !== leadId),
                total: Math.max(0, (novo[origemId]?.total || 1) - 1),
            };
            const destinoLeads = [...(novo[destinoId]?.leads || [])];
            destinoLeads.splice(destination.index, 0, leadAtualizado);
            novo[destinoId] = {
                leads: destinoLeads,
                total: (novo[destinoId]?.total || 0) + 1,
            };
            return novo;
        });

        axios.patch(`/api/leads/${leadId}/status`, { estagio_id: destinoId })
            .catch(() => {
                const filtros = filtrosParaAPIRef.current;
                fetchLeadsPorEstagio(pipelineAtiva, filtros.responsavel_id, filtros);
                showAlertModal('Erro', 'Não foi possível mover o lead.', 'danger');
            });
    };

    // ✅ totalFiltrado usa leadsPorEstagio diretamente (sem filtro local)
    const totalFiltrado = useMemo(() => {
        return Object.values(leadsPorEstagio).reduce((acc, e) => acc + (e.total || 0), 0);
    }, [leadsPorEstagio]);

    const mobileStages = pipelineAtiva?.estagios || [];

    const mobileStageIndex = mobileStages.findIndex(
        stage => String(stage.id) === String(mobileStageId)
    );

    const goToPreviousMobileStage = () => {
        if (mobileStageIndex <= 0) return;

        setMobileStageId(
            String(mobileStages[mobileStageIndex - 1].id)
        );
    };

    const goToNextMobileStage = () => {
        if (
            mobileStageIndex < 0 ||
            mobileStageIndex >= mobileStages.length - 1
        ) {
            return;
        }

        setMobileStageId(
            String(mobileStages[mobileStageIndex + 1].id)
        );
    };

    const visibleKanbanStages = isMobileKanban
        ? mobileStages.filter(
            stage => String(stage.id) === String(mobileStageId)
        )
        : mobileStages;

    const colaboradoresMarketing = useMemo(() => {
        if (!supportData?.allFuncionarios) return [];
        if (!marketingProfileIds.standard.length && !marketingProfileIds.custom.length) {
            return supportData.allFuncionarios;
        }
        const standardIds = new Set(marketingProfileIds.standard);
        const customIds   = new Set(marketingProfileIds.custom);
        return supportData.allFuncionarios.filter(f =>
            (f.standard_role_id   && standardIds.has(f.standard_role_id)) ||
            (f.entidade_perfil_id && customIds.has(f.entidade_perfil_id))
        );
    }, [supportData.allFuncionarios, marketingProfileIds]);

    const tabIcons = {
        crm_dashboard: 'chart-line',
        mk_funil_leads: 'project-diagram',
        crm_campaigns: 'bullhorn',
    };

    const handleChangeTab = useCallback((tabKey) => {
        if (tabKey !== activeTab) {
            setShowWhatsAppChat(false);
            setWhatsAppLead(null);
            setShowLeadModal(false);
            setSelectedLead(null);
        }
        setActiveTab(tabKey);
    }, [activeTab]);

    return (
        <>
            <div style={{
                backgroundColor: '#f8fafc',
                minHeight: '100vh',
                fontFamily: "'Inter', 'Segoe UI', sans-serif",
            }}>
                {/* ── Header ── */}
                <div className="marketing-page-header" style={{
                    backgroundColor: '#fff',        
                    borderBottom: '1px solid #e5e7eb',
                    padding: '0 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '64px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                    <div
                        className="marketing-header-title"
                        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                    >
                        <div style={{
                            width: '36px', height: '36px', borderRadius: '10px',
                            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FontAwesomeIcon icon="project-diagram" style={{ color: '#fff', fontSize: '16px' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                                CRM
                            </h1>
                            {currentEntidade && (
                                <p style={{ margin: 0, fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                                    {currentEntidade.nome}
                                </p>
                            )}
                        </div>
                    </div>

                    {activeTab === 'mk_funil_leads' && (
                        <div
                            className="marketing-header-actions"
                            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                        >
                            
                            {currentEntidade?.slug === 'fluxo-clinica' && (
                                <div className="marketing-header-commissions">
                                    <SalesCommissionsPanel />
                                </div>
                            )}

                            <LeadAutomationDashboardModal
                              pipelineId={pipelineAtiva?.id}
                            />
                            {pipelines.length > 1 && (
                                <div
                                    className="marketing-header-pipeline"
                                    style={{ position: 'relative' }}
                                >
                                    <select
                                        value={pipelineAtiva?.id || ''}
                                        onChange={(e) => {
                                            const p = pipelines.find(p => p.id === parseInt(e.target.value));
                                            setPipelineAtiva(p || null);
                                        }}
                                        style={{
                                            padding: '6px 32px 6px 12px',
                                            border: '1px solid #e5e7eb', borderRadius: '8px',
                                            fontSize: `calc(13px * var(--font-scale))`, color: '#374151',
                                            backgroundColor: '#fff', cursor: 'pointer',
                                            outline: 'none', appearance: 'none', minWidth: '160px',
                                        }}
                                    >
                                        {pipelines.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.is_default ? '★ ' : ''}{p.nome}
                                            </option>
                                        ))}
                                    </select>
                                    <FontAwesomeIcon icon="chevron-down" style={{
                                        position: 'absolute', right: '10px', top: '50%',
                                        transform: 'translateY(-50%)', fontSize: `calc(10px * var(--font-scale))`,
                                        color: '#9ca3af', pointerEvents: 'none',
                                    }} />
                                </div>
                            )}

                            {/* ✅ Campo de busca com debounce */}
                            <div
                                className="marketing-header-search"
                                style={{ position: 'relative' }}
                            >
                                <input
                                    type="text"
                                    placeholder="Buscar lead..."
                                    value={buscaInput}
                                    onChange={e => setBuscaInput(e.target.value)}
                                    style={{
                                        padding: '6px 32px 6px 30px',
                                        border: `1px solid ${isBuscando ? '#bfdbfe' : '#e5e7eb'}`,
                                        borderRadius: '8px',
                                        fontSize: 'calc(13px * var(--font-scale))',
                                        color: '#374151',
                                        backgroundColor: '#fff',
                                        outline: 'none',
                                        minWidth: '200px',
                                        transition: 'border-color 0.15s',
                                    }}
                                    onFocus={e => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={e => e.target.style.borderColor = isBuscando ? '#bfdbfe' : '#e5e7eb'}
                                />
                                {buscaInput && (
                                    <button
                                        onClick={() => {
                                            setBuscaInput('');
                                            setFiltrosAvancados(prev => ({ ...prev, busca: '' }));
                                            setIsBuscando(false);
                                        }}
                                        style={{
                                            position: 'absolute', right: '8px', top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none', border: 'none',
                                            cursor: 'pointer', color: '#9ca3af',
                                            padding: '0', lineHeight: '1',
                                            fontSize: 'calc(11px * var(--font-scale))',
                                        }}
                                    >
                                        <FontAwesomeIcon icon="times" />
                                    </button>
                                )}
                            </div>

                            {/* ✅ Filtros avançados */}
                            <div className="marketing-header-filter">
                                <LeadFiltros
                                    filtros={filtrosAvancados}
                                    onChange={(novosFiltros) => setFiltrosAvancados(novosFiltros)}
                                    onLimpar={() => {
                                        setFiltrosAvancados(FILTROS_INICIAIS);
                                        setBuscaInput('');
                                        setIsBuscando(false);
                                    }}
                                    totalAtivo={totalFiltrosAtivos}
                                    colaboradores={colaboradoresMarketing}
                                />  
                            </div>

                            <div
                                className="marketing-header-divider"
                                style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb' }}
                            />

                            <button
                                className="marketing-header-action-button marketing-header-settings"
                                onClick={() => setShowPipelinesConfig(true)}
                                title="Configurar Pipelines"
                                style={{
                                    width: '36px', height: '36px', borderRadius: '8px',
                                    backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
                                    cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    color: '#6b7280', fontSize: `calc(14px * var(--font-scale))`, transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.color = '#6b7280'; }}
                            >
                                <FontAwesomeIcon icon="cog" />
                            </button>

                            <button
                                className="marketing-header-action-button"
                                onClick={() => setShowImportModal(true)}
                                style={{
                                    padding: '8px 16px', backgroundColor: '#fff',
                                    color: '#374151', border: '1px solid #e5e7eb',
                                    borderRadius: '8px', cursor: 'pointer',
                                    fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                            >
                                <FontAwesomeIcon icon="file-import" />
                                Importar
                            </button>

                            <button
                                className="marketing-header-action-button"
                                onClick={handleShowCreateModal}
                                style={{
                                    padding: '8px 16px',
                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                    color: '#fff', border: 'none', borderRadius: '8px',
                                    cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    boxShadow: '0 1px 3px rgba(59,130,246,0.4)',
                                    transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <FontAwesomeIcon icon="plus" />
                                Novo Lead
                            </button>
                        </div>
                    )}
                </div>

                <div className="marketing-mobile-navigation">
                    <label htmlFor="marketing-mobile-section">
                        Seção do CRM
                    </label>

                    <select
                        id="marketing-mobile-section"
                        value={activeTab}
                        onChange={(event) => handleChangeTab(event.target.value)}
                    >
                        {permittedTabs.map(tabInfo => (
                            <option key={tabInfo.key} value={tabInfo.key}>
                                {tabInfo.title}
                            </option>
                        ))}
                    </select>
                </div>

                {/* ── Abas ── */}
                <div
                    className="marketing-desktop-navigation"
                    style={{
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #e5e7eb',
                        padding: '0 24px',
                        gap: '4px',
                    }}
                >
                    {permittedTabs.map(tabInfo => (
                        <button
                            key={tabInfo.key}
                            onClick={() => handleChangeTab(tabInfo.key)}
                            style={{
                                padding: '12px 16px', border: 'none', background: 'none',
                                cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`,
                                fontWeight: activeTab === tabInfo.key ? '600' : '400',
                                color: activeTab === tabInfo.key ? '#3b82f6' : '#6b7280',
                                borderBottom: activeTab === tabInfo.key
                                    ? '2px solid #3b82f6'
                                    : '2px solid transparent',
                                marginBottom: '-1px',
                                display: 'flex', alignItems: 'center', gap: '6px',
                                transition: 'all 0.15s', whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={e => { if (activeTab !== tabInfo.key) e.currentTarget.style.color = '#374151'; }}
                            onMouseLeave={e => { if (activeTab !== tabInfo.key) e.currentTarget.style.color = '#6b7280'; }}
                        >
                            <FontAwesomeIcon icon={tabIcons[tabInfo.key] || 'circle'} style={{ fontSize: `calc(12px * var(--font-scale))` }} />
                            {tabInfo.title}
                        </button>
                    ))}
                </div>

                {/* ── Conteúdo ── */}
                <div style={{ padding: '0' }}>
                    {activeTab === 'crm_dashboard' && (
                        <CommercialDashboard onOpenLead={handleCardClick} />
                    )}
                    {activeTab === 'crm_campaigns' && <WhatsAppCampaigns />}
                    {activeTab === 'mk_funil_leads' && (
                        <>
                            {isLoadingData || isLoadingPipeline || isLoadingSupportData || isLoadingAgendaConfig ? (
                                <div style={{
                                    display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center',
                                    padding: '80px', gap: '12px',
                                }}>
                                    <Spinner animation="border" variant="primary" />
                                    <p style={{ color: '#9ca3af', fontSize: `calc(14px * var(--font-scale))`, margin: 0 }}>
                                        Carregando dados...
                                    </p>
                                </div>
                            ) : error ? (
                                <div style={{ padding: '24px' }}>
                                    <Alert variant="danger">{error}</Alert>
                                </div>
                            ) : !pipelineAtiva ? (
                                <div style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    justifyContent: 'center', padding: '80px', gap: '16px',
                                }}>
                                    <div style={{
                                        width: '72px', height: '72px', borderRadius: '16px',
                                        backgroundColor: '#f3f4f6',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <FontAwesomeIcon icon="project-diagram" style={{ fontSize: '32px', color: '#d1d5db' }} />
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                                            Nenhuma pipeline configurada
                                        </h3>
                                        <p style={{ margin: '0 0 20px', fontSize: `calc(14px * var(--font-scale))`, color: '#9ca3af' }}>
                                            Crie uma pipeline para começar a organizar seus leads
                                        </p>
                                        <button
                                            onClick={() => setShowPipelinesConfig(true)}
                                            style={{
                                                padding: '10px 20px',
                                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                                color: '#fff', border: 'none', borderRadius: '8px',
                                                cursor: 'pointer', fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600',
                                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                            }}
                                        >
                                            <FontAwesomeIcon icon="cog" />
                                            Configurar Pipeline
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Sub-header */}
                                    <div style={{
                                        padding: '12px 24px',
                                        backgroundColor: '#fff',
                                        borderBottom: '1px solid #f3f4f6',
                                        display: 'flex', alignItems: 'center', gap: '12px',
                                    }}>
                                        <div style={{
                                            width: '10px', height: '10px', borderRadius: '50%',
                                            backgroundColor: pipelineAtiva.cor, flexShrink: 0,
                                        }} />
                                        <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
                                            {pipelineAtiva.nome}
                                        </span>
                                        <span style={{
                                            fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af',
                                            backgroundColor: '#f3f4f6',
                                            padding: '2px 8px', borderRadius: '999px',
                                        }}>
                                            {totalFiltrado} leads
                                        </span>

                                        <div style={{ display: 'flex', gap: '6px', marginLeft: '8px', flexWrap: 'wrap' }}>
                                            {(pipelineAtiva.estagios || []).map(estagio => {
                                                // ✅ usa leadsPorEstagio diretamente
                                                const count = leadsPorEstagio[estagio.id]?.total || 0;
                                                return (
                                                    <div key={estagio.id} style={{
                                                        display: 'flex', alignItems: 'center', gap: '4px',
                                                        padding: '2px 8px', borderRadius: '999px',
                                                        backgroundColor: `${estagio.cor}15`,
                                                        border: `1px solid ${estagio.cor}30`,
                                                        fontSize: `calc(11px * var(--font-scale))`, color: estagio.cor, fontWeight: '500',
                                                    }}>
                                                        <span>{estagio.nome}</span>
                                                        {count > 0 && (
                                                            <span style={{
                                                                backgroundColor: estagio.cor, color: '#fff',
                                                                borderRadius: '999px', padding: '0 5px',
                                                                fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700',
                                                            }}>
                                                                {count}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {isMobileKanban && mobileStages.length > 0 && (
                                        <div className="mobile-kanban-stage-navigation">
                                            <label htmlFor="mobile-kanban-stage">
                                                Etapa do funil
                                            </label>

                                            <select
                                                id="mobile-kanban-stage"
                                                value={mobileStageId}
                                                onChange={event => setMobileStageId(event.target.value)}
                                            >
                                                {mobileStages.map(stage => {
                                                    const total = leadsPorEstagio[stage.id]?.total || 0;

                                                    return (
                                                        <option key={stage.id} value={String(stage.id)}>
                                                            {stage.nome} · {total} lead{total !== 1 ? 's' : ''}
                                                        </option>
                                                    );
                                                })}
                                            </select>

                                            <div className="mobile-kanban-stage-actions">
                                                <button
                                                    type="button"
                                                    onClick={goToPreviousMobileStage}
                                                    disabled={mobileStageIndex <= 0}
                                                >
                                                    ← Anterior
                                                </button>

                                                <span>
                                                    {mobileStageIndex + 1} de {mobileStages.length}
                                                </span>

                                                <button
                                                    type="button"
                                                    onClick={goToNextMobileStage}
                                                    disabled={mobileStageIndex >= mobileStages.length - 1}
                                                >
                                                    Próxima →
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {/* Kanban */}
                                    <DragDropContext onDragEnd={onDragEnd}>
                                        <Container
                                            fluid
                                            className="marketing-kanban-container"
                                            style={{ padding: '20px 24px' }}
                                        >
                                            <div className={`kanban-board ${isMobileKanban ? 'mobile-single-stage' : ''}`}>
                                                    {visibleKanbanStages.map(estagio => (
                                                        <MarketingKanbanColumn
                                                        key={estagio.id}
                                                        column={{
                                                            id:    String(estagio.id),
                                                            title: estagio.nome,
                                                            color: estagio.cor,
                                                        }}
                                                        // ✅ usa leadsPorEstagio diretamente (sem filtro local)
                                                        leads={leadsPorEstagio[estagio.id]?.leads || []}
                                                        totalLeads={leadsPorEstagio[estagio.id]?.total || 0}
                                                        entidadeId={currentEntidade.id}
                                                        filtrosAvancados={filtrosAvancados}
                                                        onCardClick={handleCardClick}
                                                        onWhatsAppClick={handleWhatsAppClick}
                                                        isDragDisabled={isMobileKanban}
                                                    />
                                                ))}
                                            </div>
                                        </Container>
                                    </DragDropContext>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── Modais ── */}
            <LeadModal
                show={showLeadModal}
                onHide={() => setShowLeadModal(false)}
                lead={selectedLead}
                colaboradores={colaboradoresMarketing}
                currentUser={currentUser}
                currentEntidade={currentEntidade}
                pipelines={pipelines}
                defaultPipelineId={pipelineAtiva?.id}
                isSaving={isSavingLead}
                showAlertModal={showAlertModal}
                onSave={handleSaveLead}
                onLeadConverted={() => fetchLeadsPorEstagio(pipelineAtiva, filtrosAvancados.responsavel_id, filtrosAvancados)}
                onWhatsAppClick={handleWhatsAppClick}
                onOpenScheduleModal={(paciente) => {
                    setPacienteParaAgendar(paciente);
                    setShowAgendamentoModal(true);
                }}
            />

            <LeadImportModal
                show={showImportModal}
                onHide={() => setShowImportModal(false)}
                pipelines={pipelines}
                colaboradores={colaboradoresMarketing}
                onImportSuccess={() => {
                    const filtros = filtrosParaAPIRef.current;
                    fetchLeadsPorEstagio(pipelineAtiva, filtros.responsavel_id, filtros);
                }}
            />

            {pacienteParaAgendar && (
                <AgendamentoLeadModal
                    show={showAgendamentoModal}
                    onHide={() => setShowAgendamentoModal(false)}
                    paciente={pacienteParaAgendar}
                    lead={selectedLead}
                    especialidades={supportData.especialidades || []}
                    todosMedicos={supportData.todosMedicos || []}
                    servicos={supportData.servicos || []}
                    agendaConfig={agendaConfig}
                    onLeadUpdated={(updatedLead) => {
                        setLeadsPorEstagio(prev => {
                            const novo = { ...prev };
                            Object.keys(novo).forEach(estagioId => {
                                novo[estagioId] = {
                                    ...novo[estagioId],
                                    leads: novo[estagioId].leads.map(l =>
                                        l.id === updatedLead.id ? updatedLead : l
                                    ),
                                };
                            });
                            return novo;
                        });
                    }}
                />
            )}

            {showWhatsAppChat && whatsAppLead && (
                <WhatsAppChat
                    lead={whatsAppLead}
                    pipelineConnections={pipelineAtiva?.connections || []}
                    onClose={() => { setShowWhatsAppChat(false); setWhatsAppLead(null); }}
                    onMensagemEnviada={(leadAtualizado) => {
                        setLeadsPorEstagio(prev => {
                            const novo = { ...prev };
                            Object.keys(novo).forEach(estagioId => {
                                novo[estagioId] = {
                                    ...novo[estagioId],
                                    leads: novo[estagioId].leads.map(l =>
                                        l.id === leadAtualizado.id ? leadAtualizado : l
                                    ),
                                };
                            });
                            return novo;
                        });
                    }}
                />
            )}

            <PipelinesConfig
                show={showPipelinesConfig}
                onClose={() => {
                    setShowPipelinesConfig(false);
                    fetchPipelines();
                }}
            />
        </>
    );
};

export default CrmPage;
